import { useMemo } from 'react'

interface RoleAssignment {
  role: string
  is_home: boolean
}

interface UserActiveGameRoles {
  role_assignments?: RoleAssignment[]
  has_league_video_permission?: boolean
}

interface UseIsUserVideographerProps {
  userActiveGameRoles?: UserActiveGameRoles | null
}

interface UseIsUserVideographerResult {
  isUserVideographer: boolean
  isHomeVideographer: boolean
  isVisitorVideographer: boolean
  hasLeagueVideoPermission: boolean
}

/**
 * Hook to check if the current user is assigned as a videographer for a game.
 * Derives videographer status from game.user_active_game_roles property.
 */
export const useIsUserVideographer = ({
  userActiveGameRoles,
}: UseIsUserVideographerProps): UseIsUserVideographerResult => {
  return useMemo(() => {
    if (!userActiveGameRoles) {
      return {
        isUserVideographer: false,
        isHomeVideographer: false,
        isVisitorVideographer: false,
        hasLeagueVideoPermission: false,
      }
    }

    const { role_assignments = [], has_league_video_permission = false } =
      userActiveGameRoles

    const isHomeVideographer = role_assignments.some(
      (a) => a.role === 'videographer' && a.is_home === true
    )
    const isVisitorVideographer = role_assignments.some(
      (a) => a.role === 'videographer' && a.is_home === false
    )
    const isUserVideographer =
      has_league_video_permission || isHomeVideographer || isVisitorVideographer

    return {
      isUserVideographer,
      isHomeVideographer,
      isVisitorVideographer,
      hasLeagueVideoPermission: has_league_video_permission,
    }
  }, [userActiveGameRoles])
}
